/**
 * Renders a threaded conversation list for a helpdesk ticket, displaying
 * messages with avatars, metadata, and optional truncation for compact views.
 *
 * @module components/helpdesk/Conversation
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import {ListItemAvatar, Tooltip} from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import {dateFormat} from "../../utils/i18n";

/**
 * A functional React component that renders a conversation interface, including
 * messages, avatars, and additional styling based on user and message states.
 *
 * This component accepts props to customize the display of conversation entries and handles
 * expanded views and alternate styling for messages based on the current user context. It also
 * provides functionality for truncating messages, displaying avatar images, and formatting
 * message metadata such as the creator's name and creation date.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.helpdeskTicket - An object containing details about the helpdesk ticket, including entries.
 * @param {Array} props.helpdeskTicket.entries - The list of conversation entries to display.
 * @param {number} props.helpdeskTicket.ticket_id - The unique identifier of the helpdesk ticket.
 * @param {number} props.maxEntries - The maximum number of messages to render in the conversation view at once.
 *      If set to -1, all available messages are shown.
 * @param {boolean} props.expanded - Indicates whether the conversation is in an expanded view, disabling message truncation.
 * @param {Function} props.onExpand - A callback function triggered to expand the view and load more messages.
 * @returns {JSX.Element} The rendered conversation component, consisting of a list of messages and associated avatars.
 */
const Conversation = ({ helpdeskTicket, maxEntries, expanded, onExpand }) => {

    const user = JSON.parse(localStorage.getItem('user'));

    /**
     * Formats a message string by truncating it to a specified length
     * and appending an ellipsis (" ...") if certain conditions are met.
     *
     * The function checks if the `expanded` variable is truthy or if
     * the message length is less than 40. If either condition is true,
     * the original message is returned without any modifications.
     * Otherwise, the message is truncated to the first 37 characters,
     * and " ..." is appended.
     *
     * @param {string} message - The input string to be formatted.
     * @returns {string} The formatted message or the original message.
     */
    const formatMessage = (message) => {
        if (expanded || (message.length < 40)) {
            return message;
        }

        return message.substring(0, 37)+' ...';
    }

    /**
     * Renders a message section with formatted content, including the message text, creator's name,
     * and creation date. The display style adapts based on the message's length, the `expanded` state,
     * and the `alternate` flag.
     *
     * @param {Object} item - The message item containing data like message text, creator's name, and creation date.
     * @param {boolean} alternate - Determines whether to apply alternate styling (e.g., right alignment).
     * @returns {React.Element} The rendered component displaying the message and associated information.
     */
    const messageSection = (item, alternate) => {
        if (expanded || (item.message.length < 40)) {
            return (
                <ListItemText primary={formatMessage(item.message)} secondary={
                    <React.Fragment>
                        <Typography
                            component="small"
                            variant="body2"
                            sx={{ color: 'text.primary', display: 'inline' }}
                        >
                            {item.creator_name}
                        </Typography>
                        <br />
                        {dateFormat(item.created_at)}
                    </React.Fragment>}
                    style={alternate && {textAlign: 'right'}}
                />
            );
        }

        //console.log('Conversation Item = %o', item);
        let extraStyle = {
            typography: 'body1'
        };
        if (alternate) {
            extraStyle.textAlign = 'right';
        }
        return (
            <Tooltip title={item.message} sx={extraStyle} style={extraStyle}>
                <ListItemText primary={formatMessage(item.message)} secondary={
                    <React.Fragment>
                        <Typography
                            component="small"
                            variant="body2"
                            sx={{ color: 'text.primary', display: 'inline' }}
                        >
                            {item.creator_name}
                            <br />
                            <small>{dateFormat(item.created_at)}</small>
                        </Typography>
                    </React.Fragment>}
                    style={alternate && {textAlign: 'right'}}
                />
            </Tooltip>
        )
    }

    /**
     * Renders the avatar section for a given item with optional styling for alternate layouts.
     *
     * @param {Object} item - The data object containing information about the avatar and action.
     * @param {string} item.creator_picture_url - The URL of the creator's avatar image.
     * @param {string} item.action - The action or description associated with the avatar.
     * @param {boolean} alternate - Flag to apply an alternate layout styling to the avatar.
     * @returns {JSX.Element} The JSX element for the avatar section, including an avatar and an action text.
     */
    const avatarSection = (item, alternate) => {
        //console.log('Avatar URL = %s', item.creator_picture_url);
        return (
            <ListItemAvatar style={{color: 'text.primary', display: 'block', textAlign: 'center', margin: '0 auto'}}>
                <>
                    <Avatar src={item.creator_picture_url}
                            style={alternate ? {marginLeft: '1em', marginRight: 0, margin: '0 auto'} : {}}
                    />
                    <Typography
                        component="small"
                        variant="body2"
                        style={{textAlign: 'center', margin: '0 auto'}}
                    >
                        <small>{item.action}</small>
                    </Typography>
                </>
            </ListItemAvatar>
        )
    }

    if (!helpdeskTicket.entries || !helpdeskTicket.entries.length) {
        return (<></>);
    }

    if (!maxEntries || (maxEntries === -1)) {
        maxEntries = helpdeskTicket.entries.length;
    }

    return (
        <List flex="true" style={{display: 'flex', flexDirection: 'column', gap: '0.25em', maxHeight: '50vh', overflowY: 'auto'}}>
            {helpdeskTicket.entries.slice(0, maxEntries).map((item, idx) => {
                let itemId = `${helpdeskTicket.ticket_id}-${idx}-${Date.now()}${Date.now()*Math.E}`;
                console.log('>>>>> Conversation ItemId = %s', itemId);
                let creatorId = item?.created_by?._id?.toString() ?? item?.created_by;
                let alternate = creatorId !== user._id.toString();//dx && (idx % 2));
                if (alternate) {
                    let style = {
                        textAlign: 'right',
                        borderRadius: '10px 10px 0 10px',
                        border: '1px solid rgba(128, 128, 128, 0.25)',
                        gap: '1em',
                    };
                    if (expanded) {
                        style.backgroundColor = 'rgba(220, 220, 220, 0.25)';
                    }
                    return (
                        <ListItem alignItems="flex-start"
                            style={style}
                            id={itemId}
                            key={itemId}
                        >
                            {messageSection(item, true)}
                            {avatarSection(item, true)}
                        </ListItem>
                    );
                }
                return (
                    <ListItem alignItems="flex-start"
                        style={{borderRadius: '10px 10px 10px 0', border: '1px solid rgba(128, 128, 128, 0.25)', gap: '1em'}}
                        id={itemId}
                        key={itemId}
                    >
                        {avatarSection(item)}
                        {messageSection(item)}
                    </ListItem>
                )
            })}
            { ((helpdeskTicket?.entries?.length ?? 0) > maxEntries) && (
                <ListItem id={`${helpdeskTicket.ticket_id}--1--${Date.now()}`} onClick={onExpand}>
                    ... {helpdeskTicket?.entries?.length - maxEntries} more messages
                </ListItem>
            )}
        </List>
    );
};

Conversation.propTypes = {
    helpdeskTicket: PropTypes.object,
    maxEntries: PropTypes.number,
    expanded: PropTypes.bool,
    onExpand: PropTypes.func,
};

export default connect(null)(Conversation);
